import { getClientId } from "@smartsheet/lodestar-utils";
import { Ref } from "react";
import { useFlyout, UseFlyoutOptions } from "../../flyout";
import { CommonSelectProps } from "../types";

interface UseSelectFlyoutOptions<TargetElementType extends HTMLElement = HTMLDivElement> {
    clientId: string | undefined;
    closeMenu: () => void;
    isOpen: boolean;
    menuId: string;
    menuPlacement: CommonSelectProps<never, never>["menuPlacement"];
    menuPosition: CommonSelectProps<never, never>["menuPosition"];
    portalProps: CommonSelectProps<never, never>["portalProps"];
    targetRef: Ref<TargetElementType>;
    updateKey: UseFlyoutOptions<never>["updateKey"];
}

export const useSelectFlyout = <TargetElementType extends HTMLElement = HTMLDivElement>({
    closeMenu,
    clientId,
    isOpen,
    menuId,
    menuPlacement,
    menuPosition,
    portalProps,
    targetRef,
    updateKey,
}: UseSelectFlyoutOptions<TargetElementType>) => {
    const {
        // Ignoring most of this, we don't want the aria-owns attribute.
        targetProps: {
            ref: targetRefFromFlyout,
            // Checking downshift docs noticed "aria-owns" is only present on combobox variants
            // Testing with voice over, there is no difference in adding or removing aria-owns from select variants but, there's an a11y issue
            // if not present on combobox ones, "aria-owns" should be provided here and used in those.
            // Select variants will have a "data-menu-test-id" to allow reference in tests.
            "aria-owns": ariaOwns,
        },
        flyoutProps,
    } = useFlyout<TargetElementType>({
        id: menuId,
        clientId: getClientId(clientId, "-menu"),
        // Always true, we never hide the menu root itself, just its children.
        isOpen: true,
        // Since the menu is always open, we don't want it to be in the stacking context when "closed".
        ignoreStackOrder: !isOpen,
        onCloseRequested: closeMenu,
        targetRef,
        placement: menuPlacement ? menuPlacement : "bottom-start",
        offset: { distance: 4 },
        popperProps: {
            strategy: menuPosition,
        },
        portalProps,
        // Update positioning whenever the open state changes.
        // We need the open state because if it's closed, and something else moves the combobox, the
        // menu needs to recalc or it will still be pointing at the position of its initial render.
        updateKey,
        // closeOnEscape: false,
    });

    return {
        // The props to be spread onto the SelectMenu's Flyout.
        flyoutProps,

        // The target, which is the thing the Flyout should point at, not interact with.
        // This should be the root element of the select component.
        targetRef: targetRefFromFlyout,

        ariaOwns,
        ariaControls: ariaOwns,
    };
};
